import {
  StoreBlock,
  StoreSizeTemplate,
} from '@site-builder/common/src/types/blocks';
import { LandingTypes } from '@site-builder/common/src/types/model/landing';
import {
  StoreItem,
  DEFAULT_VC_IMAGE,
} from '@site-builder/common/src/utils/store';
import React from 'react';
import ReactDOM from 'react-dom';

import type { IStoreProps } from '../../../components/Blocks/Store';
import type { AnyStoreItemType } from '../../../utils/types';
import type { StoreAPIDataService } from './services/StoreAPIDataService';
import type { Classed } from './types';
import type { FiveSymbolLocale } from '@site-builder/common/src/utils/locale';

import { isCustomAuth } from '../../../experiments/partnersRequests/utils';
import {
  ALL_VC_TYPE_NAME,
  VI_TYPE,
  VC_TYPE,
  BUNDLE_TYPE,
  GK_TYPE,
  generateDrmList,
} from '../../../utils/store-helper';
import { addToCart } from '../../../utils/store/add-to-cart';
import { CART_QUANTITY_OPTIONS } from '../../../utils/store/cart-item-quantity';
import { getPrice } from '../../../utils/store/price-item';
import { selectDrm } from '../../../utils/store/select-drm';
import { selectQuantity } from '../../../utils/store/select-quantity';
import { lazyLoadImage } from '../../beforeBlocks/lazyLoad';
import { showPriceInButton } from '../../landingAPI/showPriceInButton';
import Store from '../../templates/Store';
import { translations } from '../../translations';
import { closeBundleInfoModal } from './bundle-modal';
import { UniversalCart } from './cart/UniversalCart';
import { unmountSectionPreloader } from './section-preloader-render';

const isGameItem = (type: string) => type === GK_TYPE;

export type IStoreProps = ReturnType<typeof collectGeneratedData>;

export const collectGeneratedData = ({
  item,
  itemSectionType,
  groupId,
  sectionIndex,
  blockStructure,
  landingType,
  enableCart,
  locale,
  cart,
  storeAPIDataService,
  block,
}: {
  item: Classed<AnyStoreItemType>;
  itemSectionType: string;
  groupId: string;
  sectionIndex: number;
  blockStructure: StoreBlock;
  landingType: LandingTypes;
  enableCart: boolean;
  locale: FiveSymbolLocale;
  cart: UniversalCart;
  storeAPIDataService: StoreAPIDataService;
  block: HTMLElement;
}) => {
  const {
    sku,
    name,
    description,
    // @ts-ignore
    long_description: longDescription,
    image_url: image,
    unit_items: drmList = [],
    content,
  } = item;

  const priceForStoreItem = getPrice(
    item,
    itemSectionType,
    landingType,
    translations['client.store.item.price_from']
  );

  const isBundleItem = StoreItem.isBundle(item);
  const isPreOrder = !!(drmList[0] && drmList[0].is_pre_order);

  const btnGroupId =
    groupId === ALL_VC_TYPE_NAME || isGameItem(itemSectionType)
      ? 'items'
      : groupId;

  const buttonId = `${
    block.id
  }-${btnGroupId}-${sku.toLocaleLowerCase()}-${sectionIndex}`.replace(
    /\./g,
    ''
  );

  let selectorList = drmList?.length ? generateDrmList(drmList) : [];
  if (!selectorList.length && isCustomAuth()) {
    selectorList = CART_QUANTITY_OPTIONS;
  }
  const showDrmList = isGameItem(itemSectionType) && selectorList.length > 1;
  const cartSku = !isGameItem(itemSectionType)
    ? sku
    : !showDrmList && selectorList.length !== 0 && selectorList[0].value;

  const buttonType = !isGameItem(itemSectionType)
    ? 'cart-button'
    : `cart-button-drm${!showDrmList ? '-one' : ''}`;

  const currentComponent = blockStructure?.components[sectionIndex];
  const descriptionEnable = currentComponent?.descriptionEnable;
  const imageSize = currentComponent?.imageSize;
  const cardSize = blockStructure?.values.cardSize;
  const showMoreEnable = isBundleItem && currentComponent?.moreInfoEnable;
  const addToCartHandler = addToCart({
    cart,
    locale,
    storeAPIDataService,
    sku,
  });
  const selector = showDrmList
    ? selectDrm(addToCartHandler)
    : selectQuantity({ cart, sku });

  const commonData = {
    name,
    description,
    longDescription,
    image: image || DEFAULT_VC_IMAGE,
    id: `${block.id}-${sku}`,
    itemSku: sku,
    itemType: itemSectionType,
    price: priceForStoreItem,
    buttonId,
    isPreOrder,
    buttonText: isPreOrder
      ? translations['client.buy_button.pre-order']
      : translations['client.buy_button.text'],
    checkoutButtonText: translations['client.buy_button.checkout'],
    addCartButtonText: translations['client.store.cart.add_button'],
    showMoreButtonText: translations['client.store.item.show_more'],
    requireAuth: item.isRequireAuth(),
    cart,
    storeAPIDataService,
    enableCart,
    cartSku,
    selectorList,
    buttonType,
    purchasedText: '',
    viType: '',
    itemGameClass: isGameItem(itemSectionType) ? 'for-game' : '',
    itemBundleClass: isBundleItem ? 'store__item-bundle' : '',
    xaElementValue: itemSectionType,
    xaExtra: JSON.stringify({
      name: sku,
      title: name,
      block: 'store',
    }),
    descriptionEnable,
    cardSize,
    isTextNeeded: cardSize === StoreSizeTemplate.SMALL || showMoreEnable,
    content,
    locale,
    imageSize,
    showMoreEnable,
    addToCart: addToCartHandler,
    handlerSelect: selector,
    selectButton: !showDrmList,
    showPriceInButton:
      showPriceInButton() &&
      cardSize === StoreSizeTemplate.SMALL &&
      !isGameItem(itemSectionType),
  };

  let generatedData = commonData;
  if (priceForStoreItem.vcPrice && landingType !== LandingTypes.TOPUP) {
    generatedData = {
      ...generatedData,
      requireAuth: true,
    };
  }

  if (itemSectionType === VI_TYPE) {
    generatedData.viType = item.virtual_item_type;
    generatedData.requireAuth = true;
    generatedData.purchasedText = translations['client.store.item.purchased'];
  }

  if (itemSectionType === VC_TYPE) {
    generatedData.viType = BUNDLE_TYPE;
    generatedData.requireAuth = true;
  }

  return generatedData;
};

export const renderEmptyTemplate = (
  target: ReactDOM.Container,
  storeItemProps: IStoreProps
) => {
  ReactDOM.hydrate(<Store {...storeItemProps} />, target);
};

export const renderStoreSection = ({
  templateDataList,
  currentSection,
  currentSectionList,
}: {
  templateDataList: IStoreProps[];
  currentSection: HTMLDivElement;
  currentSectionList: HTMLDivElement;
}) => {
  unmountSectionPreloader(currentSection);
  ReactDOM.hydrate(
    <>
      {templateDataList.map((storeItemProps) => (
        <Store
          key={storeItemProps.itemSku}
          closeBundleInfoModal={closeBundleInfoModal}
          {...storeItemProps}
        />
      ))}
    </>,
    currentSectionList
  );
  lazyLoadImage.update();
};

export const unmountStoreSection = ({ currentSectionList }) => {
  ReactDOM.unmountComponentAtNode(currentSectionList);
};
